import {
    Anchor,
    Button,
    Group,
    Loader,
    Modal,
    Paper,
    SimpleGrid,
    Stack,
    Text,
    TextInput,
    Title,
} from '@mantine/core'
import {DateInput} from '@mantine/dates'
import {Link} from '@tanstack/react-router'
import {format} from 'date-fns'
import {useState} from 'react'

import {formatBusinessTimestamp} from 'src/api/business-time'

import {parseApiDate, toLocalDate} from '../jobs/job-schedule-format'
import {formatPrice} from '../lib/format-price'
import {DetailPageBackLink} from '../ui/detail-page-back-link'
import {InvoiceStatusBadge} from './invoice-status-badge'
import {useInvoice, useRecordExternalInvoice, useVoidInvoice} from './use-invoices'

type InvoiceDetailsPageProps = {
    invoiceId: string
}

function formatCreatedAt(createdAt: string) {
    return formatBusinessTimestamp(createdAt, 'MMM d, yyyy')
}

function formatIssueDate(dateInvoiceIssued: string) {
    return format(parseApiDate(dateInvoiceIssued), 'MMM d, yyyy')
}

/** Mantine DateInput yields an ISO `YYYY-MM-DD` string; normalize to a local Date. */
function parseDateInputValue(value: string | Date | null): Date | null {
    return value == null ? null : toLocalDate(value)
}

export function InvoiceDetailsPage({invoiceId}: InvoiceDetailsPageProps) {
    const {data: invoice, isLoading, isError, error} = useInvoice(invoiceId)
    const recordExternalInvoice = useRecordExternalInvoice(invoiceId)
    const voidInvoice = useVoidInvoice(invoiceId)
    const [externalInvoiceNumber, setExternalInvoiceNumber] = useState('')
    const [dateInvoiceIssued, setDateInvoiceIssued] = useState<Date | null>(null)
    const [formError, setFormError] = useState<string | null>(null)
    const [voidModalOpened, setVoidModalOpened] = useState(false)
    const [voidError, setVoidError] = useState<string | null>(null)

    if (isLoading) {
        return (
            <Group justify="center" py="xl">
                <Loader />
            </Group>
        )
    }

    if (isError || !invoice) {
        const message = error instanceof Error ? error.message : 'Failed to load invoice'
        const isNotFound = message.toLowerCase().includes('not found')

        return (
            <Stack gap="md">
                <Title order={1}>{isNotFound ? 'Invoice not found' : 'Invoice'}</Title>
                <Text c={isNotFound ? 'dimmed' : 'red'}>
                    {isNotFound
                        ? 'This invoice may have been removed or the link is invalid.'
                        : message}
                </Text>
                {isNotFound ? <DetailPageBackLink to="/invoices" listLabel="invoices" /> : null}
            </Stack>
        )
    }

    async function handleRecordExternalDetails() {
        setFormError(null)

        const trimmedNumber = externalInvoiceNumber.trim()
        if (!trimmedNumber) {
            setFormError('QuickBooks invoice number is required')
            return
        }
        if (dateInvoiceIssued == null) {
            setFormError('Issue date is required')
            return
        }

        try {
            await recordExternalInvoice.mutateAsync({
                externalInvoiceNumber: trimmedNumber,
                dateInvoiceIssued,
            })
            setExternalInvoiceNumber('')
            setDateInvoiceIssued(null)
        } catch (mutationError) {
            setFormError(
                mutationError instanceof Error
                    ? mutationError.message
                    : 'Failed to record invoice details',
            )
        }
    }

    async function handleVoidInvoice() {
        setVoidError(null)

        try {
            await voidInvoice.mutateAsync()
            setVoidModalOpened(false)
        } catch (mutationError) {
            setVoidError(
                mutationError instanceof Error ? mutationError.message : 'Failed to void invoice',
            )
        }
    }

    const showRecordingForm = invoice.isDraft && !invoice.isVoided
    const showExternalDetails =
        !invoice.isVoided && !invoice.isDraft && invoice.externalInvoiceNumber != null
    const showVoidedExternalDetails = invoice.isVoided && invoice.externalInvoiceNumber != null

    return (
        <Stack gap="md">
            <Stack gap={4}>
                <DetailPageBackLink to="/invoices" listLabel="invoices" />

                <Group justify="space-between" align="flex-start">
                    <Stack gap={4}>
                        <Group gap="sm" align="center">
                            <Title order={1}>Invoice</Title>
                            <InvoiceStatusBadge
                                isVoided={invoice.isVoided}
                                isDraft={invoice.isDraft}
                            />
                        </Group>
                        <Text size="sm" c="dimmed">
                            Created {formatCreatedAt(invoice.createdAt)}
                        </Text>
                        {invoice.isVoided && invoice.voidedAt ? (
                            <Text size="sm" c="dimmed">
                                Voided {formatCreatedAt(invoice.voidedAt)}
                            </Text>
                        ) : null}
                    </Stack>
                    <Group gap="sm">
                        {!invoice.isVoided ? (
                            <Button
                                color="red"
                                variant="light"
                                onClick={() => setVoidModalOpened(true)}
                            >
                                Void invoice
                            </Button>
                        ) : null}
                        <Button
                            component={Link}
                            to="/projects/$projectId"
                            params={{projectId: invoice.projectId}}
                            variant="light"
                        >
                            View order
                        </Button>
                    </Group>
                </Group>
            </Stack>

            {showRecordingForm ? (
                <Paper withBorder p="md" radius="md" bg="yellow.0">
                    <Stack gap="sm">
                        <Text size="sm">
                            After sending this bill in QuickBooks, record the invoice number and
                            issue date below.
                        </Text>
                        <SimpleGrid cols={{base: 1, sm: 2}} spacing="sm">
                            <TextInput
                                label="QuickBooks invoice number"
                                value={externalInvoiceNumber}
                                onChange={(event) =>
                                    setExternalInvoiceNumber(event.currentTarget.value)
                                }
                            />
                            <DateInput
                                label="Issue date"
                                value={dateInvoiceIssued}
                                onChange={(value) =>
                                    setDateInvoiceIssued(parseDateInputValue(value))
                                }
                            />
                        </SimpleGrid>
                        {formError ? (
                            <Text size="sm" c="red">
                                {formError}
                            </Text>
                        ) : null}
                        <Group justify="flex-end">
                            <Button
                                onClick={() => void handleRecordExternalDetails()}
                                loading={recordExternalInvoice.isPending}
                            >
                                Record invoice details
                            </Button>
                        </Group>
                    </Stack>
                </Paper>
            ) : null}

            {showExternalDetails || showVoidedExternalDetails ? (
                <Paper withBorder p="md" radius="md">
                    <Stack gap="xs">
                        <Text size="sm">
                            QuickBooks invoice number:{' '}
                            <Text span fw={500}>
                                {invoice.externalInvoiceNumber}
                            </Text>
                        </Text>
                        {invoice.dateInvoiceIssued ? (
                            <Text size="sm">
                                Issue date:{' '}
                                <Text span fw={500}>
                                    {formatIssueDate(invoice.dateInvoiceIssued)}
                                </Text>
                            </Text>
                        ) : null}
                    </Stack>
                </Paper>
            ) : null}

            <Paper withBorder p="md" radius="md">
                <Stack gap="sm">
                    <Text size="sm">
                        Order:{' '}
                        <Anchor
                            component={Link}
                            to="/projects/$projectId"
                            params={{projectId: invoice.projectId}}
                            size="sm"
                        >
                            {invoice.projectName}
                        </Anchor>
                    </Text>
                    <Text size="sm">PO: {invoice.poNumber ?? 'No PO'}</Text>

                    <Stack gap="xs" mt="sm">
                        <Text size="sm" fw={500}>
                            Line items
                        </Text>
                        {invoice.lineItems.map((lineItem) => (
                            <Group key={lineItem.id} justify="space-between" wrap="nowrap">
                                <Stack gap={0}>
                                    <Text size="sm">{lineItem.name}</Text>
                                    {lineItem.jobName ? (
                                        <Text size="xs" c="dimmed" fw={500}>
                                            Job: {lineItem.jobName}
                                        </Text>
                                    ) : null}
                                    {lineItem.description ? (
                                        <Text size="xs" c="dimmed">
                                            {lineItem.description}
                                        </Text>
                                    ) : null}
                                </Stack>
                                <Text size="sm">{formatPrice(lineItem.totalPrice)}</Text>
                            </Group>
                        ))}
                    </Stack>

                    <Group justify="space-between" mt="md">
                        <Text size="sm" fw={500}>
                            Total
                        </Text>
                        <Text size="lg" fw={700}>
                            {formatPrice(invoice.total)}
                        </Text>
                    </Group>
                </Stack>
            </Paper>

            <Modal
                opened={voidModalOpened}
                onClose={() => {
                    setVoidModalOpened(false)
                    setVoidError(null)
                }}
                title="Void invoice?"
            >
                <Stack gap="md">
                    <Text size="sm">
                        Charges from this invoice will be released back to{' '}
                        <Text span fw={500}>
                            {invoice.projectName}
                        </Text>{' '}
                        and can be edited and invoiced again. This cannot be undone.
                    </Text>
                    {!invoice.isDraft ? (
                        <Text size="sm">
                            If you already sent this bill in QuickBooks, void it there as well.
                        </Text>
                    ) : null}
                    {voidError ? (
                        <Text size="sm" c="red">
                            {voidError}
                        </Text>
                    ) : null}
                    <Group justify="flex-end">
                        <Button variant="default" onClick={() => setVoidModalOpened(false)}>
                            Cancel
                        </Button>
                        <Button
                            color="red"
                            onClick={() => void handleVoidInvoice()}
                            loading={voidInvoice.isPending}
                        >
                            Void invoice
                        </Button>
                    </Group>
                </Stack>
            </Modal>
        </Stack>
    )
}
